import {
  Typography,
  Box,
  Avatar,
  Card,
  Grid,
  useTheme,
  styled
} from '@mui/material';

import { useTranslation } from 'react-i18next';
import ReceiptTwoToneIcon from '@mui/icons-material/ReceiptTwoTone';
import ArrowUpwardTwoToneIcon from '@mui/icons-material/ArrowUpwardTwoTone';
import SupportTwoToneIcon from '@mui/icons-material/SupportTwoTone';
import YardTwoToneIcon from '@mui/icons-material/YardTwoTone';
import SnowmobileTwoToneIcon from '@mui/icons-material/SnowmobileTwoTone';
import { OrdersReportDailySummary } from 'src/services/apiService/response-models/report';

const AvatarWrapper = styled(Avatar)(
  ({ theme }) => `
      color:  ${theme.colors.alpha.trueWhite[100]};
      width: ${theme.spacing(5.5)};
      height: ${theme.spacing(5.5)};
`
);

interface OrderSalesSummaryProps {
  summary?: OrdersReportDailySummary;
}

function OrderSalesSummary({ summary }: OrderSalesSummaryProps) {
  const { t }: { t: any } = useTranslation();
  const theme = useTheme();

  const grand = summary?.grandOrderSummary;

  return (
    <Grid container spacing={4}>
      <Grid item xs={12} sm={6} lg={4}>
        <Card
          sx={{
            px: 3,
            pb: 6,
            pt: 3
          }}
        >
          <Box display="flex" alignItems="center">
            <AvatarWrapper
              sx={{
                background: `${theme.colors.gradients.blue4}`
              }}
            >
              <ReceiptTwoToneIcon fontSize="small" />
            </AvatarWrapper>
            <Typography
              sx={{
                ml: 1.5,
                fontSize: `${theme.typography.pxToRem(15)}`,
                fontWeight: 'bold'
              }}
              variant="subtitle2"
              component="div"
            >
              {t('Orders')}
            </Typography>
          </Box>
          <Box
            display="flex"
            alignItems="center"
            sx={{
              ml: -2,
              pt: 2,
              pb: 1.5,
              justifyContent: 'center'
            }}
          >
            <ArrowUpwardTwoToneIcon
              sx={{
                color: `${theme.colors.error.main}`
              }}
            />
            <Typography
              sx={{
                pl: 1,
                fontSize: `${theme.typography.pxToRem(35)}`
              }}
              variant="h1"
            >
              {grand ? grand.ordersCount : '-'}
            </Typography>
          </Box>
          <Typography
            align="center"
            variant="body2"
            noWrap
            color="text.secondary"
            component="div"
          >
            {/* Placeholder for growth/compare info */}
          </Typography>
        </Card>
      </Grid>
      <Grid item xs={12} sm={6} lg={4}>
        <Card
          sx={{
            px: 3,
            pb: 6,
            pt: 3
          }}
        >
          <Box display="flex" alignItems="center">
            <AvatarWrapper
              sx={{
                background: `${theme.colors.gradients.orange3}`
              }}
            >
              <SupportTwoToneIcon fontSize="small" />
            </AvatarWrapper>
            <Typography
              sx={{
                ml: 1.5,
                fontSize: `${theme.typography.pxToRem(15)}`,
                fontWeight: 'bold'
              }}
              variant="subtitle2"
              component="div"
            >
              {t('Net Sales')}
            </Typography>
          </Box>
          <Box
            display="flex"
            alignItems="center"
            sx={{
              ml: -2,
              pt: 2,
              pb: 1.5,
              justifyContent: 'center'
            }}
          >
            <ArrowUpwardTwoToneIcon
              sx={{
                color: `${theme.colors.success.main}`
              }}
            />
            <Typography
              sx={{
                pl: 1,
                fontSize: `${theme.typography.pxToRem(35)}`
              }}
              variant="h1"
            >
              {grand ? `$${grand.netTotal?.toLocaleString()}` : '-'}
            </Typography>
          </Box>
          <Typography
            align="center"
            variant="body2"
            noWrap
            color="text.secondary"
            component="div"
          >
            {/* Placeholder for growth/compare info */}
          </Typography>
        </Card>
      </Grid>
      <Grid item xs={12} sm={6} lg={4}>
        <Card
          sx={{
            px: 3,
            pb: 6,
            pt: 3
          }}
        >
          <Box display="flex" alignItems="center">
            <AvatarWrapper
              sx={{
                background: `${theme.colors.success.main}`
              }}
            >
              <YardTwoToneIcon fontSize="small" />
            </AvatarWrapper>
            <Typography
              sx={{
                ml: 1.5,
                fontSize: `${theme.typography.pxToRem(15)}`,
                fontWeight: 'bold'
              }}
              variant="subtitle2"
              component="div"
            >
              {t('Gross Sales')}
            </Typography>
          </Box>
          <Box
            display="flex"
            alignItems="center"
            sx={{
              ml: -2,
              pt: 2,
              pb: 1.5,
              justifyContent: 'center'
            }}
          >
            <ArrowUpwardTwoToneIcon
              sx={{
                color: `${theme.colors.success.main}`
              }}
            />
            <Typography
              sx={{
                pl: 1,
                fontSize: `${theme.typography.pxToRem(35)}`
              }}
              variant="h1"
            >
              {grand ? `$${grand.orderTotal?.toLocaleString()}` : '-'}
            </Typography>
          </Box>
          <Typography
            align="center"
            variant="body2"
            noWrap
            color="text.secondary"
            component="div"
          >
            {/* Placeholder for growth/compare info */}
          </Typography>
        </Card>
      </Grid>
      <Grid item xs={12} sm={6} lg={3}>
        <Card
          sx={{
            px: 3,
            pb: 6,
            pt: 3
          }}
        >
          <Box display="flex" alignItems="center">
            <AvatarWrapper
              sx={{
                background: `${theme.colors.primary.main}`
              }}
            >
              <SnowmobileTwoToneIcon fontSize="small" />
            </AvatarWrapper>
            <Typography
              sx={{
                ml: 1.5,
                fontSize: `${theme.typography.pxToRem(15)}`,
                fontWeight: 'bold'
              }}
              variant="subtitle2"
              component="div"
            >
              {t('Discounts')}
            </Typography>
          </Box>
          <Box
            display="flex"
            alignItems="center"
            sx={{
              ml: -2,
              pt: 2,
              pb: 1.5,
              justifyContent: 'center'
            }}
          >
            <ArrowUpwardTwoToneIcon
              sx={{
                color: `${theme.colors.warning.main}`
              }}
            />
            <Typography
              sx={{
                pl: 1,
                fontSize: `${theme.typography.pxToRem(35)}`
              }}
              variant="h1"
            >
              {grand ? `$${grand.totalDiscount?.toLocaleString()}` : '-'}
            </Typography>
          </Box>
          <Typography
            align="center"
            variant="body2"
            noWrap
            color="text.secondary"
            component="div"
          >
            {/* Placeholder for growth/compare info */}
          </Typography>
        </Card>
      </Grid>
      <Grid item xs={12} sm={6} lg={3}>
        <Card sx={{ px: 3, pb: 6, pt: 3 }}>
          <Box display="flex" alignItems="center">
            <AvatarWrapper sx={{ background: `${theme.colors.info.main}` }}>
              <SupportTwoToneIcon fontSize="small" />
            </AvatarWrapper>
            <Typography sx={{ ml: 1.5, fontSize: `${theme.typography.pxToRem(15)}`, fontWeight: 'bold' }} variant="subtitle2" component="div">
              {t('Tax')}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" sx={{ ml: -2, pt: 2, pb: 1.5, justifyContent: 'center' }}>
            <ArrowUpwardTwoToneIcon sx={{ color: `${theme.colors.info.main}` }} />
            <Typography sx={{ pl: 1, fontSize: `${theme.typography.pxToRem(35)}` }} variant="h1">
              {grand ? `$${grand.totalTax?.toLocaleString()}` : '-'}
            </Typography>
          </Box>
        </Card>
      </Grid>
      <Grid item xs={12} sm={6} lg={3}>
        <Card sx={{ px: 3, pb: 6, pt: 3 }}>
          <Box display="flex" alignItems="center">
            <AvatarWrapper sx={{ background: `${theme.colors.warning.main}` }}>
              <SupportTwoToneIcon fontSize="small" />
            </AvatarWrapper>
            <Typography sx={{ ml: 1.5, fontSize: `${theme.typography.pxToRem(15)}`, fontWeight: 'bold' }} variant="subtitle2" component="div">
              {t('Quantity')}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" sx={{ ml: -2, pt: 2, pb: 1.5, justifyContent: 'center' }}>
            <ArrowUpwardTwoToneIcon sx={{ color: `${theme.colors.warning.main}` }} />
            <Typography sx={{ pl: 1, fontSize: `${theme.typography.pxToRem(35)}` }} variant="h1">
              {grand ? grand.quantity ?? '-' : '-'}
            </Typography>
          </Box>
        </Card>
      </Grid>
      <Grid item xs={12} sm={12} lg={3}>
        <Card sx={{ px: 3, pb: 6, pt: 3 }}>
          <Box display="flex" alignItems="center">
            <AvatarWrapper sx={{ background: `${theme.colors.error.main}` }}>
              <SupportTwoToneIcon fontSize="small" />
            </AvatarWrapper>
            <Typography sx={{ ml: 1.5, fontSize: `${theme.typography.pxToRem(15)}`, fontWeight: 'bold' }} variant="subtitle2" component="div">
              {t('Avg Order Value')}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" sx={{ ml: -2, pt: 2, pb: 1.5, justifyContent: 'center' }}>
            <ArrowUpwardTwoToneIcon sx={{ color: `${theme.colors.error.main}` }} />
            <Typography sx={{ pl: 1, fontSize: `${theme.typography.pxToRem(35)}` }} variant="h1">
              {grand ? `$${grand.averageOrderValue?.toLocaleString()}` : '-'}
            </Typography>
          </Box>
        </Card>
      </Grid>
     
    </Grid>
  );
}

export default OrderSalesSummary;
